/**
 * OrderView — упорядочивание элементов (kind="order").
 *
 * Элементы `kindData.items` (уже перемешаны автором; верный порядок скрыт —
 * анти-чит) показываются списком; игрок меняет порядок кнопками «вверх/вниз»
 * (без drag-and-drop — простой и надёжный UI). «Ответить» →
 * onAnswer({kind:'order', data:{order}}), где order — перестановка индексов
 * `items` в выбранной игроком последовательности.
 *
 * На reveal показывается верный порядок; позиции, угаданные игроком,
 * помечаются зелёным, ошибочные — красным. Spectate (ведущий, Фаза 10 И3):
 * read-only список элементов без кнопок ↑/↓; после reveal — верный порядок.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isOrderReveal, orderItems, type PlayerAnswer } from '../../protocol';
import type { QuestionBodyProps } from './types';
import { FormulaText } from '../FormulaText';

export function OrderView({
  question,
  variant,
  myAnswer,
  reveal,
  onAnswer,
}: QuestionBodyProps): JSX.Element {
  const { t } = useTranslation();
  const items = orderItems(question);

  const buildInitial = (): number[] => {
    const given = myAnswer?.kind === 'order' ? myAnswer.data.order : null;
    const identity = items.map((_, i) => i);
    if (given && given.length === items.length && isPermutation(given, items.length)) return given;
    return identity;
  };
  const [seq, setSeq] = useState<number[]>(buildInitial);
  const [submitted, setSubmitted] = useState(myAnswer?.kind === 'order');

  const locked = variant === 'answer' && (submitted || reveal !== null);
  const revealData = reveal?.reveal && isOrderReveal(reveal.reveal) ? reveal.reveal : null;

  function move(index: number, dir: -1 | 1): void {
    if (locked) return;
    const target = index + dir;
    if (target < 0 || target >= seq.length) return;
    setSeq((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function handleSubmit(): void {
    if (locked) return;
    setSubmitted(true);
    const answer: PlayerAnswer = { kind: 'order', data: { order: seq } };
    onAnswer(answer);
  }

  if (variant === 'spectate') {
    // Баг-О (Фаза 10, И3): read-only предпросмотр для ведущего — список
    // элементов задания без кнопок перемещения и «Ответить»; после reveal —
    // верный порядок (correctOrder). Анти-чит: до reveal — только kindData.
    const shown = revealData ? revealData.correctOrder : items.map((_, i) => i);
    return (
      <div className="order-view">
        <ol className="order-view__list">
          {shown.map((itemIdx, pos) => (
            <li key={itemIdx} className="order-view__item">
              <span className="order-view__pos">{pos + 1}.</span>
              <FormulaText className="order-view__text" text={items[itemIdx] ?? `#${itemIdx}`} />
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className="order-view">
      <ol className="order-view__list">
        {seq.map((itemIdx, pos) => {
          const correctIdx = revealData?.correctOrder[pos] ?? null;
          const matched = revealData !== null && itemIdx === correctIdx;
          const wrong = revealData !== null && !matched;
          const classes = ['order-view__item'];
          if (matched) classes.push('order-view__item--correct');
          if (wrong) classes.push('order-view__item--wrong');
          return (
            <li key={itemIdx} className={classes.join(' ')}>
              <span className="order-view__pos">{pos + 1}.</span>
              <FormulaText className="order-view__text" text={items[itemIdx] ?? `#${itemIdx}`} />
              <span className="order-view__controls">
                <button
                  type="button"
                  className="btn btn--ghost order-view__btn"
                  disabled={locked || pos === 0}
                  onClick={() => move(pos, -1)}
                  aria-label={t('order.moveUpAria', { item: items[itemIdx] ?? '' })}
                  data-testid={`order-up-${pos}`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn--ghost order-view__btn"
                  disabled={locked || pos === seq.length - 1}
                  onClick={() => move(pos, 1)}
                  aria-label={t('order.moveDownAria', { item: items[itemIdx] ?? '' })}
                  data-testid={`order-down-${pos}`}
                >
                  ↓
                </button>
              </span>
              {revealData ? (
                <span className="order-view__verdict">
                  {matched ? (
                    '✓'
                  ) : (
                    <FormulaText
                      text={t('order.verdictWrong', { item: items[correctIdx ?? -1] ?? '—' })}
                    />
                  )}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>

      <div className="open-view__row">
        {submitted && !revealData ? (
          <span className="q-hint q-hint--ok">{t('open.answered')}</span>
        ) : null}
        <button type="button" className="cta" disabled={locked} onClick={handleSubmit}>
          {t('common.answer')}
        </button>
      </div>
    </div>
  );
}

/** true, если arr — перестановка чисел 0..n-1. */
function isPermutation(arr: number[], n: number): boolean {
  if (arr.length !== n) return false;
  const seen = new Set<number>();
  for (const v of arr) {
    if (v < 0 || v >= n || seen.has(v)) return false;
    seen.add(v);
  }
  return true;
}
